import { useEffect } from "react";

import Link from "@tiptap/extension-link";
import TextStyle from "@tiptap/extension-text-style";
import TiptapUnderline from "@tiptap/extension-underline";
import { EditorContent, BubbleMenu as TiptapBubbleMenu, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Bold,
  Heading1,
  Heading2,
  Heading3,
  Image as ImageIcon,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Strikethrough,
  Underline,
} from "lucide-react";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/button";
import { api, cn, handleApiRequest } from "@/lib/utils";
import useRouterQuery from "../hooks/useRouterQuery";
import { CustomImageExtension } from "./extensions/custom-image";
import { handleImageUpload } from "./extensions/utils";

import type { Editor } from "@tiptap/react";

/* eslint-disable no-unused-vars */

interface TiptapEditorProps {
  className?: string;
  initialContent?: string;
  onUpdate: (html: string) => void;
  editorContentClassName?: string;
  buttons?: React.ReactNode;
}

const TiptapEditor = ({ className, initialContent, onUpdate, editorContentClassName, buttons }: TiptapEditorProps) => {
  const websiteId = useRouterQuery("websiteId") as string;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        bulletList: {
          HTMLAttributes: {
            class: "list-disc pl-7 space-y-2",
          },
        },
        orderedList: {
          HTMLAttributes: {
            class: "list-decimal pl-7 space-y-2",
          },
        },
        listItem: {
          HTMLAttributes: {
            class: "not-prose space-y-2",
          },
        },
        dropcursor: {
          class: "text-ash-500",
        },
      }),
      TextStyle,
      TiptapUnderline,
      CustomImageExtension({
        upload: async (file) => {
          const url = await handleImageUpload(file, websiteId);
          return url;
        },
        delete: async (id) => {
          await handleApiRequest({
            makeRequest: async () => {
              const response = await api.delete("/api/uploads/delete", {
                data: { images: [{ providerId: id, source: "imagekit" }], websiteId },
              });
              return response.data;
            },
            successCallback: (data: { message: string }) => {
              toast.success(data.message);
            },
          });
        },
        cancel: () => {},
      }),
      Link.configure({
        linkOnPaste: true,
        defaultProtocol: "https",
        protocols: ["http", "https", "mailto", "tel"],
        openOnClick: true,
        HTMLAttributes: {
          class: "text-blue-600 hover:text-blue-800 hover:cursor-pointer",
        },
      }),
    ],
    editorProps: {
      attributes: {
        class: cn(
          "prose prose-slate max-w-full focus:outline-hidden min-h-96 w-full p-4",
          "prose-headings:font-medium prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl",
          "prose-p:leading-7 prose-a:text-primary prose-a:hover:text-primary/80",
          "prose-img:rounded-md prose-img:shadow-md"
        ),
      },
    },
    content: initialContent || "",
    onUpdate: ({ editor }: { editor: Editor }) => {
      const html = editor.getHTML();
      onUpdate(html);
    },
    immediatelyRender: false,
  });

  // Add effect to update editor content when initialContent changes
  useEffect(() => {
    if (editor && initialContent !== undefined && initialContent !== editor.getHTML()) {
      editor.commands.setContent(initialContent);
    }
  }, [editor, initialContent]);

  return (
    <div
      className={cn(
        "relative w-full h-full border rounded-lg bg-white",
        "focus-within:ring-2 focus-within:ring-offset-2",
        className
      )}
    >
      <ToolbarMenu editor={editor} buttons={buttons} />
      <BubbleMenu editor={editor} />

      <EditorContent
        editor={editor}
        className={cn("w-full flex-1", editorContentClassName)}
        onChange={() => {
          const html = editor?.getHTML() || "";
          onUpdate(html);
        }}
      />
    </div>
  );
};

function ToolbarMenu({ editor, buttons }: { editor: Editor | null; buttons?: React.ReactNode }) {
  if (!editor) return null;

  return (
    <div className="z-[1px] flex items-center gap-1 p-2 border-b bg-white justify-between">
      <Button
        variant="ghost"
        size="sm"
        type="button"
        title="Add Image"
        onClick={() => {
          editor?.chain().focus().insertImageComponent({ event: "insert" }).run();
        }}
      >
        <ImageIcon className="h-4 w-4" />
      </Button>
      {buttons}
    </div>
  );
}

function BubbleMenu({ editor }: { editor: Editor | null }) {
  if (!editor) return null;

  return (
    <TiptapBubbleMenu
      editor={editor}
      tippyOptions={{ duration: 100 }}
      className="bg-white shadow-lg rounded-lg border border-ash-200 p-1.5"
    >
      <div className="flex flex-col gap-1.5 items-center">
        {/* top row - text styling */}
        <div className="flex items-center gap-0.5">
          <Button
            variant="ghost"
            title="Bold"
            type="button"
            size="sm"
            onClick={() => editor?.chain().focus().toggleBold().run()}
            className={cn(
              "h-8 w-8 p-1 hover:bg-ash-100 rounded-md",
              editor?.isActive("bold") && "bg-ash-100 text-ash-900"
            )}
          >
            <Bold className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            type="button"
            title="Italic"
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            className={cn(
              "h-8 w-8 p-1 hover:bg-ash-800/10 rounded-md",
              editor?.isActive("italic") && "bg-ash-800/10 text-ash-900"
            )}
          >
            <Italic className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            type="button"
            title="Underline"
            onClick={() => editor?.chain().focus().toggleUnderline().run()}
            className={cn(
              "h-8 w-8 p-1 hover:bg-ash-100 rounded-md",
              editor?.isActive("underline") && "bg-ash-100 text-ash-900"
            )}
          >
            <Underline className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            type="button"
            title="Strikethrough"
            onClick={() => editor?.chain().focus().toggleStrike().run()}
            className={cn(
              "h-8 w-8 p-1 hover:bg-ash-100 rounded-md",
              editor?.isActive("strike") && "bg-ash-100 text-ash-900"
            )}
          >
            <Strikethrough className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            type="button"
            title="Link"
            onClick={() => {
              const url = window.prompt("Enter URL");
              if (url) {
                editor?.chain().focus().setLink({ href: url }).run();
              }
            }}
            className={cn(editor?.isActive("link") && "bg-ash-300")}
          >
            <LinkIcon className="h-4 w-4" />
          </Button>
        </div>

        {/* bottom row - headings, lists, and media */}
        <div className="flex items-center gap-0.5">
          <Button
            variant="ghost"
            title="Heading 1"
            size="sm"
            type="button"
            onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
            className={cn(
              "h-8 w-8 p-1 hover:bg-ash-100 rounded-md",
              editor?.isActive("heading", { level: 1 }) && "bg-ash-100 text-ash-900"
            )}
          >
            <Heading1 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            title="Heading 2"
            size="sm"
            type="button"
            onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
            className={cn(
              "h-8 w-8 p-1 hover:bg-ash-100 rounded-md",
              editor?.isActive("heading", { level: 2 }) && "bg-ash-100 text-ash-900"
            )}
          >
            <Heading2 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            title="Heading 3"
            size="sm"
            type="button"
            onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
            className={cn(
              "h-8 w-8 p-1 hover:bg-ash-100 rounded-md",
              editor?.isActive("heading", { level: 3 }) && "bg-ash-100 text-ash-900"
            )}
          >
            <Heading3 className="h-4 w-4" />
          </Button>

          <div className="h-8 w-px bg-ash-200 mx-1" />

          <Button
            variant="ghost"
            size="sm"
            type="button"
            title="Bullet List"
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            className={cn(
              "h-8 w-8 p-1 hover:bg-ash-100 rounded-md",
              editor?.isActive("bulletList") && "bg-ash-100 text-ash-900"
            )}
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            type="button"
            title="Ordered List"
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            className={cn(
              "h-8 w-8 p-1 hover:bg-ash-100 rounded-md",
              editor?.isActive("orderedList") && "bg-ash-100 text-ash-900"
            )}
          >
            <ListOrdered className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </TiptapBubbleMenu>
  );
}

export default TiptapEditor;
